import { useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  // MenuItem,
} from "@mui/material";

import { getCategory } from "../utils/categories";
import { API_URL } from "../config/api";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdded: (emailSent: boolean) => void;
};

export default function AddJudokaDialog({
  open,
  onClose,
  onAdded,
}: Props) {
  const [birthDate, setBirthDate] = useState("");
  // const [birthYear, setBirthYear] = useState(1900);
  const [licence, setLicence] = useState("");

  const [email, setEmail] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [gender, setGender] = useState<"M" | "F">("M");

  const [nationality, setNationality] = useState("");

  const [clubNumber, setClubNumber] = useState("");
  const [clubName, setClubName] = useState("");

  const category = birthDate
    ? getCategory(new Date(birthDate).getFullYear())
    : "";

  const noCompetition = category === "U21+";

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    !submitting &&
    emailValid &&
    !!lastName.trim() &&
    !!firstName.trim() &&
    !!gender &&
    !!nationality.trim() &&
    !!birthDate &&
    !noCompetition &&
    !!clubName.trim();

  const handleAdd = async () => {
  const registrationCode =
    localStorage.getItem("registrationCode");

  if (!registrationCode || submitting) {
    return;
  }

  setSubmitting(true);

  try {
    const response = await fetch(
      `${API_URL}/registration/${registrationCode}/judokas`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          licence,
          firstName,
          lastName,
          gender,
          nationality,
          birthDate,
          category,
          clubNumber,
          clubName,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Erreur API");
    }

    const created = await response.json();

    // Les champs propres au judoka sont vidés ; email, nom, nationalité
    // et club restent pour enchaîner facilement frères et sœurs.
    setFirstName("");
    setBirthDate("");
    setLicence("");

    onAdded(!!created.emailSent);

    onClose();

  } catch (err) {
    console.error(err);
  } finally {
    setSubmitting(false);
  }
};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Ajouter un judoka
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email"
              value={email}
              error={!!email && !emailValid}
              helperText={
                !!email && !emailValid
                  ? "Adresse email invalide"
                  : undefined
              }
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Licence (facultatif pour les judokas étrangers)"
              value={licence}
              onChange={(e) => setLicence(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Nom"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Prénom"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <RadioGroup row
              value={gender}
              onChange={(e) => setGender(e.target.value as "M" | "F")}
            >
              <FormControlLabel
                value="M"
                control={<Radio />}
                label="Garçon"
              />

              <FormControlLabel
                value="F"
                control={<Radio />}
                label="Fille"
              />
            </RadioGroup>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Nationalité"
              value={nationality}
              onChange={(e) =>
                setNationality(e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              type="date"
              label="Date de naissance"
              InputLabelProps={{
                shrink: true,
              }}
              value={birthDate}
              error={noCompetition}
              onChange={(e) =>
                setBirthDate(e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              disabled
              label="Catégorie"
              value={category}
              error={noCompetition}
              helperText={
                noCompetition
                  ? "Aucune compétition disponible pour les U21+"
                  : undefined
              }
              sx={
                noCompetition
                  ? {
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: (theme) =>
                          theme.palette.error.main,
                      },
                      "& .MuiInputLabel-root.Mui-disabled, & .MuiFormHelperText-root.Mui-disabled":
                        {
                          color: "error.main",
                        },
                      "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "error.main",
                        },
                    }
                  : undefined
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="N° Club"
              value={clubNumber}
              onChange={(e) =>
                setClubNumber(e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Nom du club"
              value={clubName}
              onChange={(e) =>
                setClubName(e.target.value)
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Annuler
        </Button>

        <Button
          variant="contained"
          color="error"
          disabled={!canSubmit}
          onClick={handleAdd}
        >
          {submitting ? "Ajout en cours..." : "Ajouter"}
        </Button>
      </DialogActions>
    </Dialog >
  );
}